<template>
 <div class="player-info-bar" v-if="players.length">
  <div class="player-cards">
    <div
      v-for="player in players"
      :key="player.id"
      class="player-card"
    >
      <div class="player-header">
        <div class="player-basic">
          <img :src="player.photo" alt="" class="player-img" />
          <div>
            <div class="player-name">{{ player.name }}</div>
            <div class="player-rating" v-if="player.st_rating > 0"><span :style="{ backgroundColor: ratingColor(player.st_rating) }">{{ player.st_rating }}</span> Rating</div>
          </div>
        </div>
        <button class="close-btn" @click="removePlayer(player.id)">×</button>
      </div>

      <div class="player-card-content">
        <!-- Minutes Played (always visible) -->
        <div class="d-flex jc_space-between borderbottom padd12">
           <div class="d-flex space-between gap16"><span class="material-icons">timer</span>Minutes Played</div>
           <div class="d-flex space-between">{{ player.minutes }}'</div>
        </div>

        <!-- Match Heatmap (always visible) -->
        <div class="heatmap-section">
          <div class="heatmap-header" @click="toggleHeatmap(player.id)">
            <div class="d-flex space-between gap16">
              <span class="material-icons" style="font-size:18px;">location_on</span>
              <span>Match heatmap</span>
            </div>
            <span class="material-icons heatmap-chevron" :class="{ 'chevron-up': getHeatmapOpen(player.id) }">expand_more</span>
          </div>
          <div v-if="getHeatmapOpen(player.id)">
            <FootballHeatmap
              :points="player.heatmap?.heatmap || []"
              :playerPosition="player.heatmap?.playerposition || ''"
              :teamSide="player.heatmap?.teamside || 'home'"
              :formation="player.formation"
            />
          </div>
        </div>

        <!-- Statistics header with toggle -->
        <div class="stats-toggle-header">
          <div class="stats-toggle-title">Statistics</div>
          <div class="stats-toggle-btns">
            <button
              class="toggle-icon-btn"
              :class="{ 'toggle-icon-active': getViewMode(player.id) === 'stats' }"
              @click="setViewMode(player.id, 'stats')"
            >
              <span class="material-icons">format_list_bulleted</span>
            </button>
            <button
              class="toggle-icon-btn"
              :class="{ 'toggle-icon-active': getViewMode(player.id) === 'field' }"
              @click="setViewMode(player.id, 'field')"
            >
              <span class="material-icons">sports_soccer</span>
            </button>
          </div>
        </div>

        <!-- ═══════ STATS LIST VIEW ═══════ -->
        <template v-if="getViewMode(player.id) === 'stats'">
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Goals</div>
               <div class="d-flex space-between">{{ player.goals }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff borderbottom">
               <div class="d-flex space-between">Assists</div>
               <div class="d-flex space-between">{{ player.assists }}</div>
          </div>

          <!-- Goalkeeper-only stats -->
          <template v-if="isGoalkeeper(player)">
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Total Saves</div>
                 <div class="d-flex space-between">{{ player.saves }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Goals prevented</div>
                 <div class="d-flex space-between">{{ player.goalsPrevented }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Saves from inside box</div>
                 <div class="d-flex space-between">{{ player.savesfrominsidebox }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff borderbottom">
                 <div class="d-flex space-between">Punches</div>
                 <div class="d-flex space-between">{{ player.punches }}</div>
            </div>
          </template>

          <!-- Outfield player stats -->
          <template v-if="!isGoalkeeper(player)">
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Total Shots</div>
                 <div class="d-flex space-between">{{ player.shots_total }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Shots on Target</div>
                 <div class="d-flex space-between">{{ player.shots_on_goal }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Big Chances Missed</div>
                 <div class="d-flex space-between">{{ player.big_chance_missed }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Penalties missed</div>
                 <div class="d-flex space-between">{{ player.penaltyMiss }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff borderbottom">
                 <div class="d-flex space-between">Offsides</div>
                 <div class="d-flex space-between">{{ player.offsides }}</div>
            </div>
          </template>

          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Tackles (wons)</div>
               <div class="d-flex space-between">{{ player.totalTackle }}({{ player.wonTackle }})</div>
          </div>
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Interceptions</div>
               <div class="d-flex space-between">{{ player.interceptionWon }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Clearances</div>
               <div class="d-flex space-between">{{ player.clearances }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Blocked shots</div>
               <div class="d-flex space-between">{{ player.outfielderBlock }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Recoveries</div>
               <div class="d-flex space-between">{{ player.ballRecovery }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Ground duels (won)</div>
               <div class="d-flex space-between">{{ player.groundduels }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Aerial duels (won)</div>
               <div class="d-flex space-between">{{ player.arealduels }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff borderbottom">
               <div class="d-flex space-between">Fouls</div>
               <div class="d-flex space-between">{{ player.fouls }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Key Passes</div>
               <div class="d-flex space-between">{{ player.keypassess }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff borderbottom">
               <div class="d-flex space-between">Blocks</div>
               <div class="d-flex space-between">{{ player.blocks }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Crosses (accurate)</div>
               <div class="d-flex space-between">{{ player.crocess }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Accurate Passes</div>
               <div class="d-flex space-between">{{ player.accuratepass }}</div>
          </div>
          <div class="d-flex jc_space-between padd-diff">
               <div class="d-flex space-between">Passes in opposition half (acc.)</div>
               <div class="d-flex space-between">{{ player.accurateOppositionHalfPasses }}/{{ player.totalOppositionHalfPasses }}({{ player.opossitionhalfPercentage }})</div>
          </div>
          <div class="d-flex jc_space-between padd-diff borderbottom">
               <div class="d-flex space-between">Passes in own half (acc.)</div>
               <div class="d-flex space-between">{{ player.accurateOwnHalfPasses }}/{{ player.totalOwnHalfPasses }}({{ player.ownHalfPercentage }})</div>
          </div>
        </template>

        <!-- ═══════ FIELD MAP VIEW ═══════ -->
        <template v-if="getViewMode(player.id) === 'field'">
          <FootballPlayerShotmap
            :shots="(player.shotmap && player.shotmap.shotmap) || []"
            :passes="(player.shotmap && player.shotmap.passes) || []"
            :dribbles="[].concat((player.shotmap && player.shotmap.dribbles) || [], (player.shotmap && player.shotmap['ball-carries']) || [])"
            :defActions="(player.shotmap && player.shotmap.defensive) || []"
            :playerGoals="player.goals || 0"
            :playerAssists="player.assists || 0"
            :playerTouches="player.touches || 0"
            :playerDefContributions="(player.totalTackle || 0) + (player.interceptionWon || 0) + (player.clearances || 0)"
            :teamSide="(player.heatmap && player.heatmap.teamside) || 'home'"
            @tab-change="setActiveMapTab(player.id, $event)"
          />

          <!-- Tab-specific stats below the map -->
          <!-- SHOT stats -->
          <template v-if="getActiveMapTab(player.id) === 'shot'">
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Goals</div>
                 <div class="d-flex space-between">{{ player.goals }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Total Shots</div>
                 <div class="d-flex space-between">{{ player.shots_total }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Shots on Target</div>
                 <div class="d-flex space-between">{{ player.shots_on_goal }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Big Chances Missed</div>
                 <div class="d-flex space-between">{{ player.big_chance_missed }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Penalties missed</div>
                 <div class="d-flex space-between">{{ player.penaltyMiss }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff borderbottom">
                 <div class="d-flex space-between">Offsides</div>
                 <div class="d-flex space-between">{{ player.offsides }}</div>
            </div>
          </template>

          <!-- PASS stats -->
          <template v-if="getActiveMapTab(player.id) === 'pass'">
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Assists</div>
                 <div class="d-flex space-between">{{ player.assists }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Key Passes</div>
                 <div class="d-flex space-between">{{ player.keypassess }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Accurate Passes</div>
                 <div class="d-flex space-between">{{ player.accuratepass }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Crosses (accurate)</div>
                 <div class="d-flex space-between">{{ player.crocess }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Blocks</div>
                 <div class="d-flex space-between">{{ player.blocks }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Passes in opposition half (acc.)</div>
                 <div class="d-flex space-between">{{ player.accurateOppositionHalfPasses }}/{{ player.totalOppositionHalfPasses }}({{ player.opossitionhalfPercentage }})</div>
            </div>
            <div class="d-flex jc_space-between padd-diff borderbottom">
                 <div class="d-flex space-between">Passes in own half (acc.)</div>
                 <div class="d-flex space-between">{{ player.accurateOwnHalfPasses }}/{{ player.totalOwnHalfPasses }}({{ player.ownHalfPercentage }})</div>
            </div>
          </template>

          <!-- DRIB stats -->
          <template v-if="getActiveMapTab(player.id) === 'drib'">
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Ground duels (won)</div>
                 <div class="d-flex space-between">{{ player.groundduels }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff borderbottom">
                 <div class="d-flex space-between">Aerial duels (won)</div>
                 <div class="d-flex space-between">{{ player.arealduels }}</div>
            </div>
          </template>

          <!-- DEF stats -->
          <template v-if="getActiveMapTab(player.id) === 'def'">
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Tackles (wons)</div>
                 <div class="d-flex space-between">{{ player.totalTackle }}({{ player.wonTackle }})</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Interceptions</div>
                 <div class="d-flex space-between">{{ player.interceptionWon }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Clearances</div>
                 <div class="d-flex space-between">{{ player.clearances }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Blocked shots</div>
                 <div class="d-flex space-between">{{ player.outfielderBlock }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff">
                 <div class="d-flex space-between">Recoveries</div>
                 <div class="d-flex space-between">{{ player.ballRecovery }}</div>
            </div>
            <div class="d-flex jc_space-between padd-diff borderbottom">
                 <div class="d-flex space-between">Fouls</div>
                 <div class="d-flex space-between">{{ player.fouls }}</div>
            </div>
          </template>
        </template>

      </div>
    </div>
  </div>
</div>

</template>

<script>
import FootballHeatmap from './FootballHeatmap.vue';
import FootballPlayerShotmap from './FootballPlayerShotmap.vue';
export default {
  name: "PlayerInfoBar",
  data() {
    return {
      players: [],
      viewModes: {},
      activeMapTabs: {},
      heatmapOpen: {}
    };
  },
  components: { FootballHeatmap, FootballPlayerShotmap },
  methods: {
    ratingColor(rating) {
      const r = parseFloat(rating);
      if (isNaN(r)) return '#f72537';
      if (r >= 9.0) return '#21a35a';        // GREEN  (9.0 - 10.0)
      if (r >= 8.0) return '#5bc0eb';        // LIGHT BLUE (8.0 - 8.9)
      if (r >= 7.0) return '#f5821f';        // ORANGE (7.0 - 7.9)
      if (r >= 6.0) return '#f5c518';        // YELLOW (6.0 - 6.9)
      return '#f72537';                      // RED    (1.0 - 5.9)
    },
    openPlayer(player) {
      const exists = this.players.find(p => p.id === player.id);
      if (!exists) {
        this.players.push(player);
        this.$set(this.viewModes, player.id, 'stats');
        this.$set(this.activeMapTabs, player.id, 'shot');
        this.$set(this.heatmapOpen, player.id, true);
      }
    },
    removePlayer(id) {
      this.players = this.players.filter(p => p.id !== id);
      this.$delete(this.viewModes, id);
      this.$delete(this.activeMapTabs, id);
      this.$delete(this.heatmapOpen, id);
    },
    getViewMode(playerId) {
      return this.viewModes[playerId] || 'stats';
    },
    setViewMode(playerId, mode) {
      this.$set(this.viewModes, playerId, mode);
    },
    getActiveMapTab(playerId) {
      return this.activeMapTabs[playerId] || 'shot';
    },
    setActiveMapTab(playerId, tab) {
      this.$set(this.activeMapTabs, playerId, tab);
    },
    getHeatmapOpen(playerId) {
      return this.heatmapOpen[playerId] !== false;
    },
    toggleHeatmap(playerId) {
      this.$set(this.heatmapOpen, playerId, !this.getHeatmapOpen(playerId));
    },
    isGoalkeeper(player) {
      var pos = (player.position || player.playerPosition || '').toString().toUpperCase();
      if (pos === 'G' || pos === 'GK' || pos === 'GOALKEEPER') return true;
      // Also check heatmap playerposition
      if (player.heatmap && player.heatmap.playerposition) {
        var hp = player.heatmap.playerposition.toString().toUpperCase();
        if (hp === 'G' || hp === 'GK' || hp === 'GOALKEEPER') return true;
      }
      return false;
    }
  }
};
</script>

<style scoped>
.player-info-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  padding: 10px;
  overflow-x: auto;
  gap: 10px;

  z-index: 5;
  pointer-events: none;
}

.player-cards {
  display: flex;
  flex-direction: row-reverse;
  gap: 10px;

  pointer-events: auto;
}

.player-card {
  width: 350px;
  height: 380px;
  background: white;
  color: black;
  border-radius: 12px 12px 0 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);

  pointer-events: auto;
}

.player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #eee;
  background: #fff;
  border-radius: 10px 10px 0 0;
  color: black;
  font-weight: bold;
}
.player-rating{font-size: 15px; margin-top: 8px;}
.player-rating span{
  background-color: #f72537; /* fallback; overridden by dynamic ratingColor() */
  color: white;
  padding: 5px;
  border-radius: 4px;
  margin-right: 3px;

}
.player-basic {
  display: flex;
  align-items: center;
  gap: 10px;
}

.player-img {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  object-fit: cover;
}

.player-card-content {
  flex: 1;
  overflow-y: auto;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: black;
}

/* ── Heatmap Section ─────────────────── */
.heatmap-section {
  border-bottom: 1px solid #eee;
}
.heatmap-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  user-select: none;
}
.heatmap-header:hover {
  background: #fafafa;
}
.heatmap-chevron {
  font-size: 20px;
  color: #888;
  transition: transform 0.2s;
}
.chevron-up {
  transform: rotate(180deg);
}

/* ── Statistics Toggle Header ────────── */
.stats-toggle-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
}
.stats-toggle-title {
  font-size: 15px;
  font-weight: 600;
  color: #222;
}
.stats-toggle-btns {
  display: flex;
  gap: 4px;
  background: #f0f0f0;
  border-radius: 8px;
  padding: 3px;
}
.toggle-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: #999;
  transition: all 0.2s;
}
.toggle-icon-btn .material-icons {
  font-size: 18px;
}
.toggle-icon-btn:hover {
  color: #555;
}
.toggle-icon-active {
  background: #1a1a2e !important;
  color: #fff !important;
}

.jc_space-between{justify-content: space-between; align-items: center;}
.gap16{gap:16px;}
.borderbottom{border-bottom: 1px solid #eee;}
.padd12{padding: 12px;}
.padd-diff{padding-inline: 16px;padding-block: 8px;}
.player-card-content div {
    font-size: 14px;
}
</style>
